"use client";

import { useId } from "react";
import { cn } from "@/lib/utils";

// Field — the accessible form-field primitive shared by every contribution
// form (observation upload, expert review, validation). It guarantees:
//  - a real <label htmlFor> / control id pairing
//  - hint and error text wired through aria-describedby
//  - error text in a role="alert" region announced to screen readers
//
// The control is supplied via a render prop so the id and aria-describedby
// land on the actual <input>/<select>/<textarea>, with no cloneElement magic.
export function Field({
  label,
  hint,
  error,
  required = false,
  className,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  className?: string;
  children: (props: { id: string; describedBy: string | undefined }) => React.ReactNode;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;

  return (
    <div className={cn("space-y-1.5", className)}>
      <label htmlFor={id} className="block font-body-md text-body-md font-semibold text-on-surface">
        {label}
        {required && (
          <span className="text-error" aria-hidden="true">
            {" *"}
          </span>
        )}
      </label>
      {hint && (
        <p id={hintId} className="font-body-md text-body-md text-on-surface-variant">
          {hint}
        </p>
      )}
      {children({ id, describedBy })}
      {error && (
        <p id={errorId} role="alert" className="font-body-md text-body-md text-error">
          {error}
        </p>
      )}
    </div>
  );
}
